import React, { useState } from 'react';
import { Alert, Form, InputGroup } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { User, removeConnection } from '../../services/userService';
import ConnectionCard from './ConnectionCard';
import LoadingSpinner from '../common/LoadingSpinner'; // Reusable spinner
import ErrorAlert from '../common/ErrorAlert'; // Reusable error display

interface ConnectionListProps {
    connections: User[];
}

const ConnectionList: React.FC<ConnectionListProps> = ({ connections }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const queryClient = useQueryClient();

    // --- Mutation for Removing Connection --- 
    const { 
        mutate: removeUserConnection, 
        isLoading: isRemoving, 
        error: removeError 
    } = useMutation(removeConnection, {
        onSuccess: (data, removedUserId) => {
            console.log('Connection removed successfully:', data);
            // Invalidate the connections query to refetch the updated list
            queryClient.invalidateQueries('connections');
            // Optionally, show a success toast/notification
        },
        onError: (error: any) => {
            console.error('Failed to remove connection:', error);
            // Error message is handled by the ErrorAlert component below
        },
    });

    const handleRemove = (userId: number) => {
        // Optional: Add confirmation dialog here
        // if (window.confirm('Are you sure you want to remove this connection?')) { ... }
        removeUserConnection(userId);
    };

    // --- Filtering Logic (Client-side) ---
    // Backend already filters, but this adds instant feedback
    const filteredConnections = connections.filter(conn => 
        (conn.name && conn.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conn.profession && conn.profession.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conn.company && conn.company.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div>
            {/* Search Bar */} 
            <InputGroup className="mb-4">
                <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                <Form.Control
                    type="search"
                    placeholder="Search connections by name, profession, or company..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            {/* Display Removal Error */} 
            {removeError && (
                <ErrorAlert message={(removeError as any).message || 'Failed to remove connection.'} />
            )}

            {/* Display Removal Loading State */} 
            {isRemoving && (
                <div className="text-center my-3">
                    <LoadingSpinner size="sm" text="Removing connection..." />
                </div>
            )}

            {/* Connection Cards */} 
            {filteredConnections.length > 0 ? (
                filteredConnections.map(connection => (
                    <ConnectionCard 
                        key={connection.id} 
                        connection={connection} 
                        onRemove={handleRemove} 
                    />
                ))
            ) : searchTerm ? (
                <Alert variant="info">No connections found matching "{searchTerm}".</Alert>
            ) : (
                <Alert variant="info">You currently have no connections. Explore the map to find professionals!</Alert>
            )}
        </div>
    );
};

export default ConnectionList; 